import { BufferStop, bufferStopSchema } from "@opstel/query/models/bufferStop";
import { createSchema, ZodOutputFor } from "@opstel/query/zod-utils";
import { PlainMessage } from "@bufbuild/protobuf";
import { Location as ProtoLocation } from "@opstel/gen/knop/ui/v1/model/location_pb";
import { z } from "zod/v3";
import { Junction, junctionSchema } from "@opstel/query/models/junction";
import { TrackConnection, trackConnectionSchema } from "@opstel/query/models/trackConnection";
import { Crossing, crossingSchema } from "@opstel/query/models/crossing";
import { TrackWithGeometry, trackWithGeometrySchema } from "@opstel/query/models/track";
import { BoundingBox } from "@opstel/geo/BoundingBox";
import { TrackGroupWithGeometry, trackGroupWithGeometrySchema } from "./trackGroup";
import { ScalingVector } from "@opstel/geo/Point";

export type StablingInfraLookup = Record<string, StablingInfra>;
export type StablingInfra = TrackWithGeometry | TrackGroupWithGeometry;
export interface Location {
  name: string;
  junctions: Junction[];
  bufferStops: BufferStop[];
  trackConnections: TrackConnection[];
  crossings: Crossing[];
  tracks: TrackWithGeometry[];
  trackGroups: TrackGroupWithGeometry[];
  stablingInfraLookup: StablingInfraLookup;
}

function createStablingInfraLookup(
  tracks: TrackWithGeometry[],
  trackGroups: TrackGroupWithGeometry[],
): StablingInfraLookup {
  return Object.fromEntries([
    ...tracks.map((track) => [track.code, track]),
    ...trackGroups.map((trackGroup) => [trackGroup.code, trackGroup]),
  ]) as StablingInfraLookup;
}

export const locationSchema = createSchema<Location, PlainMessage<ProtoLocation>>({
  name: z.string(),
  junctions: z.array(junctionSchema),
  bufferStops: z.array(bufferStopSchema),
  trackConnections: z.array(trackConnectionSchema),
  crossings: z.array(crossingSchema),
  tracks: z.array(trackWithGeometrySchema),
  trackGroups: z.array(trackGroupWithGeometrySchema),
}).transform((location) => ({
  ...location,
  stablingInfraLookup: createStablingInfraLookup(location.tracks, location.trackGroups),
})) satisfies ZodOutputFor<Location>;

export const Location = {
  calculateBoundingBox: (
    location: Location,
    scalingFactor: ScalingVector | null = null,
  ): BoundingBox => {
    return BoundingBox.findFromCoordinates(
      [
        ...location.junctions.map((junction) => junction.coordinate),
        ...location.crossings.map((crossing) => crossing.coordinate),
        ...location.trackConnections.map((trackConnection) => trackConnection.coordinate),
        ...location.bufferStops.map((bufferStop) => bufferStop.coordinate),
        ...location.tracks.map((track) => track.coordinates.fromSourceToTarget).flat(),
      ].map((i) => (scalingFactor === null ? i : i.scale(scalingFactor))),
    );
  },
};
